<template>
  <div id="homeProductsSection" class="pb-4 md:pb-9">
    <div class="app-container-full-width-mobile">
      <!-- main category -->
      <div class="flex justify-between items-center px-5 md:px-0">
        <h3 class="font-bold text-[0.82rem] text-[#000000] sm:text-[1rem] lg:text-[17px] 3xl:text-[18px] mb-1.5 capitalize md:mb-2">
          {{ $t('user.popular_products') }}
        </h3>
      </div>
      <!-- categories list -->
      <div :class="`relative show-arrow-when-hover `">
        <div class="flex flex-wrap gap-x-1 gap-y-2 justify-between items-center">
          <ListSortingSelector
              :isProcessing="isUpdatingProducts"
              :selectedItemId="sortProductsBy"
              :sortingOptions="sortingOptions"
              @onSelect="loadProductsSortedBy"
          />

          <div class="px-5 md:px-0">
            <!-- Display method dropdown -->
            <Multiselect :allow-empty="false"
                         :class="`filter__box min-w-[11.875rem] h-[2.188rem] bg-white ${isDisplayMethodFilterOpen ? 'focused' : ''}`"
                         :close-on-select="true"
                         :modelValue="filterProductsByType"
                         :options="[allTypeOption, ...productTypeOptions]"
                         :placeholder="$t('general.products_display_method')"
                         :searchable="false"
                         :show-labels="false"
                         label="text"
                         track-by="value"
                         @close="() => isDisplayMethodFilterOpen = false"
                         @open="() => isDisplayMethodFilterOpen = true"
                         @select="handleAllOption"
                         @update:modelValue="changeProductByTypeValue"
                         :aria-label="$t('general.products_display_method')"
                         :aria-expanded="`${isDisplayMethodFilterOpen ? 'true' : 'false'}`"
                         aria-controls="listbox-null"
                         :aria-owns="isDisplayMethodFilterOpen ? 'listbox-null' : ''"
            >
            </Multiselect>
          </div>
        </div>
      </div>
    </div>
    <!-- products -->
    <div class="new-home-products-container-full-width-mobile">
      <div v-if="isInitiallyLoadingProducts" :aria-label="$t('general.loading')" class="mt-10 text-light-3 text-center">
        <ul class="products-items-list">
          <li
              v-for="(_, index) in Array.from({ length: skeletonNumbers })"
              :key="index"
          >
            <div
                class="card__two h-[288px] w-full min-w-[130px] animate-pulse overflow-hidden rounded-[7px] border border-[#DEDEDE]"
            >
              <!-- visual info -->
              <div class="relative">
                <!-- store bar -->
                <div
                    class="absolute left-0 top-0 flex w-full flex-nowrap items-center gap-1.5 bg-gray-200 px-3 py-2 text-[11px] font-normal text-[#FFFFFF] sm:text-[12px] 3xl:text-[13px]"
                >
                  <span
                      class="store__logo grid h-[22px] w-[22px] place-items-center rounded-full bg-gray-300 p-1 sm:h-[23px] sm:w-[23px] 3xl:h-[24px] 3xl:w-[24px]"
                  />
                  <span
                      class="h-[12px] w-[52px] overflow-hidden text-ellipsis whitespace-nowrap bg-gray-300"
                  />
                </div>
                <!-- product image -->
                <div class="z-0 h-[179px] w-[100%] bg-gray-100"/>
                <!-- product rating -->
                <span
                    class="absolute bottom-2.5 left-auto right-2 flex h-[25px] w-[56px] items-center gap-1.5 rounded-[6px] bg-gray-300 text-[12px] text-[#FFFFFF] rtl:left-2 rtl:right-auto sm:text-[13px] 3xl:text-[14px]"
                >
                  <strong/>
                </span>
              </div>
              <div class="px-3">
                <!-- price, description, favorite and production date -->
                <div>
                  <div
                      class="flex h-[40px] w-full items-center justify-between"
                  >
                    <!-- price -->
                    <div
                        class="3xl:text[19px] flex items-center gap-1 text-[17px] sm:text-[18px]"
                    >
                      <strong class="h-[16px] w-[77px] bg-gray-200 font-bold"/>
                      <span class="h-[8px] w-[23px] bg-gray-100"/>
                    </div>
                    <div></div>
                  </div>
                </div>
                <!-- description -->
                <div
                    class="h product-description ellipsis-x2 h-[30px] w-[90%] bg-gray-200 px-2 text-[13px] font-normal text-black sm:text-[14px] 3xl:text-[15px]"
                >
                  <p class="transition-all duration-150"/>
                </div>
                <!-- date -->
                <div class="mt-3 h-[11px] w-[70%] bg-gray-200 px-2"/>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div v-else-if="itemsList && itemsListWithAvailableImages.length > 0"
           :class="`relative mt-4 show-arrow-when-hover`">
        <TransitionGroup v-if="allowRenderingProductsList" id="productsItemsList" :class="`${isUpdatingProducts ? 'disabled' : ''} products-items-list`" name="products-list"
                         style="margin: -10px !important;"
                         tag="ul">
          <li v-for="item in itemsList" :key="item.id"
              :class="`${(item.ad && item.ad_type !== 'product') ? 'col-span-full' : ''}`"
              :data-product-id="item.id"
              @click="saveProductCardPosition(item.id)">
            <!-- Store Ad item -->
            <StoreAdItem v-if="item.ad && item.ad_type === 'store'" :item="item"/>
            <!-- Store & product Ad item -->
            <StoreProductAdItem v-else-if=" item.ad && item.ad_type === 'store_product'" :item="item"/>
            <!-- Regular product item -->
            <ProductItem v-else :favoriteProductsIDs="favoriteProductsIDs" :portalPage="portalPage" :item="item" :withAds="true"/>
          </li>
        </TransitionGroup>
      </div>
      <div v-else class="mt-10 text-light-3 text-center">
        {{ $t('panel.empty_prodducts') }}
      </div>
    </div>
  </div>
</template>

<script>
import {CustomImage, CustomItemsSlider} from "@/components";
import ProductItem from "../productItemDesigns/ProductItem.vue";
import StoreAdItem from "../adItems/StoreAdItem.vue";
import StoreProductAdItem from "../adItems/StoreProductAdItem.vue";
import ListSortingSelector from "../general/ListSortingSelector.vue";

import {mapState} from 'pinia';
import {useGlobalStore} from '@/store/Modules/global';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';


export default {
  name: 'NewHomeSection',
  setup() {
    const allowRenderingProductsList = ref(true);
    const usedCookieLanguage = useCookie('localStorageCopy')?.value?.language || 'ar';
    const forceRerenderSwiper = async () => {
      allowRenderingProductsList.value = false;
      await nextTick();
      allowRenderingProductsList.value = true;
    };
    return {
      usedCookieLanguage,
      allowRenderingProductsList,
      forceRerenderSwiper
    }
  },
  data() {
    return {
      selectedDisplayMethod: {value: "wholesale", text: this.$t("panel.wholesale")},
      isDisplayMethodFilterOpen: false,
      skeletonNumbers: 10,
    };
  },
  components: {
    CustomItemsSlider,
    ProductItem,
    CustomImage,
    Multiselect,
    StoreAdItem,
    StoreProductAdItem,
    ListSortingSelector
  },
  props: {
    itemsList: {
      type: Array,
      required: true,
      default: () => [],
    },
    sortProductsBy: {
      type: [String, Number],
      required: true,
      default: "all",
    },
    loadProductsSortedBy: {
      type: Function,
      required: true
    },
    isUpdatingProducts: {
      type: Boolean,
      required: true,
      default: false,
    },
    isInitiallyLoadingProducts: {
      type: Boolean,
      required: true,
      default: false,
    },
    filterProductsByType: {
      type: [Object, null],
      required: true,
      default: () => null,
    },
    changeProductByTypeValue: {
      type: Function,
      required: true
    },
    portalPage: {
      type: String,
      required: false,
      default: null,
    },
  },
  methods: {
    redirectToImgUrl(imgUrl) {
      imgUrl && window.open(imgUrl, '_blank')
    },
    handleAllOption(selected) {
      (selected?.value == this.allTypeOption?.value) && this.changeProductByTypeValue(null);
    },
    
    // Save product card position when clicked
    saveProductCardPosition(productId) {
      console.log(`📍 Product card clicked: ${productId}`);
      this.$emit('saveProductCardPosition', productId);
    }
  },
  computed: {
    ...mapState(useGlobalStore, {
      favoriteProductsIDs: (store) => store?.getFavoriteProducts || [],
    }),
    allTypeOption() {
      return {value: 'all', text: this.$t('general.all')}
    },
    productTypeOptions() {
      return [
        {value: "retail", text: this.$t("panel.retail")},
        {value: "wholesale", text: this.$t("panel.wholesale")},
        {value: "offer", text: this.$t("panel.offer")},
      ];
    },
    sortingOptions() {
      return [
        {value: "all", text: this.$t("general.list_sorting.all")},
        {value: "date", text: this.$t("general.list_sorting.date")},
        {value: "views", text: this.$t("general.list_sorting.views")},
        {value: "sales", text: this.$t("general.list_sorting.sales")},
        {value: "rating", text: this.$t("general.list_sorting.rating")},
        {value: "discount", text: this.$t("general.list_sorting.discount")},
      ]
    },
    itemsListWithAvailableImages() {
      return this.itemsList?.filter(product => product?.image) || [];
    }
  },
  watch: {
    itemsListWithAvailableImages() {
      this.forceRerenderSwiper();
    }
  }
};
</script>

<style lang="scss" scoped >
#homeProductsSection {
  position: relative;
  z-index: 1; /* Raise for mobile overlay protection */
  isolation: isolate;
  $multiselectHeight: 2.375rem;

  .multiselect__select {
    min-height: $multiselectHeight;
  }

  .multiselect__select::before {
    border-color: #0963ff transparent transparent transparent !important;
  }

  .multiselect__tags {
    min-height: $multiselectHeight;
  }

  .multiselect__tags.multiselect__tags,
  .multiselect__single.multiselect__single {
    background: #ffffff;

  }

  .multiselect__placeholder {
    color: black;
    font-weight: bold;
    font-size: 0.80rem;
  }

  .main-attr-container .multiselect__tags.multiselect__tags,
  .main-attr-container .multiselect__single.multiselect__single {
    background: #f3f3f3;
  }

  .multiselect__single.multiselect__single {
    font-size: 15px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .arrow-shadow {
    filter: drop-shadow(0 0 10px rgba(0, 0, 0, .3));
  }

  .show-arrow-when-hover:hover .show-arrow {
    opacity: 1;
  }

  .show-arrow:hover {
    opacity: 1;
  }

  $loaderSize: 33px;

  .products_loader {
    width: $loaderSize;
    height: $loaderSize;
    display: inline-block;
    position: relative;
  }

  .products_loader::after,
  .products_loader::before {
    content: '';
    box-sizing: border-box;
    width: $loaderSize;
    height: $loaderSize;
    border-radius: 50%;
    background: var(--primary-lighter-clr);
    position: absolute;
    left: 0;
    top: 0;
    animation: animloader 2s linear infinite;
  }

  .products_loader::after {
    animation-delay: 1s;
  }

  @keyframes animloader {
    0% {
      transform: scale(0);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 0;
    }
  }
}


</style>
